import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from './api';

const AuthContext = createContext(null);

const USER_CACHE_KEY = 'ind4_user';

function readCachedUser() {
  try {
    const raw = localStorage.getItem(USER_CACHE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function writeCachedUser(user) {
  try {
    if (user) localStorage.setItem(USER_CACHE_KEY, JSON.stringify(user));
    else localStorage.removeItem(USER_CACHE_KEY);
  } catch {
    // ignore quota errors
  }
}

export function AuthProvider({ children }) {
  // Optimistically hydrate from cache so a Cloudflare / gateway hiccup on
  // /auth/me does not blank out the app and force a re-login.
  const [user, setUser] = useState(() => (localStorage.getItem('ind4_token') ? readCachedUser() : null));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('ind4_token');
    if (!token) {
      setLoading(false);
      return;
    }

    let cancelled = false;

    // Retry /auth/me a few times on transient failures (network / 5xx /
    // Cloudflare "empty response"). Only an explicit 401 clears the session.
    const refresh = async () => {
      const delays = [0, 2500, 6000];
      for (let i = 0; i < delays.length; i += 1) {
        if (cancelled) return;
        if (delays[i]) await new Promise(r => setTimeout(r, delays[i]));
        try {
          const res = await api.get('/auth/me');
          if (cancelled) return;
          setUser(res.data);
          writeCachedUser(res.data);
          setLoading(false);
          return;
        } catch (err) {
          if (cancelled) return;
          const status = err?.response?.status;
          if (status === 401 && i === delays.length - 1) {
            // 401 on every attempt — the token is definitively bad.
            localStorage.removeItem('ind4_token');
            writeCachedUser(null);
            setUser(null);
            setLoading(false);
            return;
          }
          // Transient error (undefined status, 5xx, network, Cloudflare) or
          // an isolated 401 during a backend rollover — keep the token and
          // any cached user, then retry.
        }
      }
      // After all retries failed with transient errors: keep the token +
      // cached user in place; just stop the initial loading spinner. The
      // user stays logged in with whatever we have cached (may be null if
      // there was no prior cache — the app will still send the token on
      // subsequent requests and recover when the backend is reachable).
      if (!cancelled) setLoading(false);
    };

    refresh();
    return () => { cancelled = true; };
  }, []);

  const login = useCallback(async (email, password) => {
    const res = await api.post('/auth/login', { email, password });
    localStorage.setItem('ind4_token', res.data.token);
    writeCachedUser(res.data.user);
    setUser(res.data.user);
    return res.data.user;
  }, []);

  const register = useCallback(async (data) => {
    const res = await api.post('/auth/register', data);
    localStorage.setItem('ind4_token', res.data.token);
    writeCachedUser(res.data.user);
    setUser(res.data.user);
    return res.data.user;
  }, []);

  const viewAs = useCallback(async (id) => {
    const res = await api.post(`/admin/showcase-accounts/${id}/view`);
    localStorage.setItem('ind4_admin_token', localStorage.getItem('ind4_token'));
    localStorage.setItem('ind4_admin_user', localStorage.getItem(USER_CACHE_KEY) || '');
    localStorage.setItem('ind4_token', res.data.token);
    writeCachedUser(res.data.user);
    setUser(res.data.user);
  }, []);

  const returnToAdmin = useCallback(() => {
    const token = localStorage.getItem('ind4_admin_token');
    if (!token) return;
    localStorage.setItem('ind4_token', token);
    localStorage.setItem(USER_CACHE_KEY, localStorage.getItem('ind4_admin_user') || '');
    localStorage.removeItem('ind4_admin_token');
    localStorage.removeItem('ind4_admin_user');
    setUser(readCachedUser());
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem('ind4_admin_token');
    localStorage.removeItem('ind4_admin_user');
    localStorage.removeItem('ind4_token');
    writeCachedUser(null);
    setUser(null);
  }, []);

  const updateUser = useCallback((updatedUser) => {
    writeCachedUser(updatedUser);
    setUser(updatedUser);
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, updateUser, viewAs, returnToAdmin }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
